import React, { useState, useCallback } from 'react';
import { Share2, Link, MessageCircle, Send, X, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const ShareButton = ({ activity, className = '' }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/activity/${activity.id}`;
  const shareText = `Check out ${activity.title} on InfoBel!`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  const handleNativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: activity.title, text: shareText, url: shareUrl });
      } catch (err) { /* user cancelled */ }
    } else {
      setShowDialog(true);
    }
  }, [activity.title, shareUrl, shareText]);

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      color: '#25D366',
      icon: <MessageCircle className="w-5 h-5" />,
      url: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    },
    {
      name: 'Facebook',
      color: '#1877F2',
      icon: <span className="text-sm font-bold">f</span>,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: 'X / Twitter',
      color: '#000000',
      icon: <span className="text-sm font-bold">X</span>,
      url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      name: 'Email',
      color: '#666B67',
      icon: <Send className="w-5 h-5" />,
      url: `mailto:?subject=${encodedText}&body=${encodedText}%20${encodedUrl}`,
    },
  ];

  return (
    <>
      <button
        onClick={handleNativeShare}
        className={`w-10 h-10 rounded-full bg-white/90 flex items-center justify-center ${className}`}
        data-testid="share-activity-btn"
      >
        <Share2 className="w-5 h-5 text-[#2C302E]" />
      </button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Share this activity</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-4 py-4">
            {shareOptions.map((opt) => (
              <a
                key={opt.name}
                href={opt.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2"
                data-testid={`share-${opt.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: opt.color }}>
                  {opt.icon}
                </div>
                <span className="text-xs text-[#666B67]">{opt.name}</span>
              </a>
            ))}
          </div>
          {/* Copy link */}
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-3 p-3 bg-[#F9F8F6] rounded-xl hover:bg-[#E07A5F]/10 transition-colors"
            data-testid="copy-link-btn"
          >
            {copied ? <Check className="w-5 h-5 text-[#81B29A]" /> : <Link className="w-5 h-5 text-[#666B67]" />}
            <span className="text-sm text-[#2C302E] flex-1 text-left truncate">{shareUrl}</span>
            <span className="text-xs text-[#E07A5F] font-medium">{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareButton;
